import { Field } from '../PreviewFields';
import { PreviewHeader } from '../PreviewHeader';

export interface BankFormValues {
  accountNumber: string;
  ifsc: string;
}

export interface BankAccountScreenProps {
  /** Live form values — pass from the session runtime; omit in the builder mock. */
  values?: BankFormValues;
  onChange?: (patch: Partial<BankFormValues>) => void;
  /** Validation messages (session runtime) — shown under each field. */
  accountError?: string;
  ifscError?: string;
  /**
   * Which of the four methods is active. Used ONLY to caption the screen for the
   * builder — it is never shown to the applicant.
   *
   * "Penny Drop - Basic", "Penniless - Advance" and the rest are our internal
   * product names for how the check is performed. They mean nothing to the person
   * entering their account number, and printing them on an applicant's screen leaks
   * our provider strategy for no benefit. The builder still needs to see which
   * method a step resolved to, so the name goes under the phone (the `label`
   * caption) instead of inside it.
   */
  method?: string;
  /** True for the Penny Drop methods — shows the ₹1 penny-drop note. */
  pennyDrop?: boolean;
}

// Bank Account Verification (SEBI spec §8) — the applicant enters their account
// number + IFSC; the provider confirms the account is active and (with an earlier
// PAN/Aadhaar step) the name matches. OWN interim design — swap for the designer's
// screen when it arrives. All 4 methods (Penny Drop Basic/Advance, Penniless
// Basic/Advance) share this same input form.
export function BankAccountScreen({
  values,
  onChange,
  accountError,
  ifscError,
  pennyDrop,
}: BankAccountScreenProps) {
  return (
    <>
      <PreviewHeader title="Bank Account Verification" subtitle="Enter your bank account details" />
      {/* Says what the applicant will actually SEE happen in their bank account —
          a ₹1 credit — without naming the technique. "Penny drop" is our internal
          name for it; on the applicant's screen it reads as jargon and tells them
          nothing they need. */}
      {pennyDrop && (
        <div className="mb-2 rounded-lg border border-[#DBE5FF] bg-[#EEF3FF] px-3 py-2 text-[length:var(--text-12)] text-[#0048FF]">
          We will deposit ₹1 into your account to confirm it is active. The amount is yours to keep.
        </div>
      )}
      <Field
        label="Account Number"
        placeholder="32100004512345"
        inputMode="numeric"
        hint="9–18 digits"
        value={values?.accountNumber}
        onChange={onChange ? (v) => onChange({ accountNumber: v.replace(/\D/g, '') }) : undefined}
        error={accountError}
      />
      <Field
        label="IFSC Code"
        placeholder="SBIN0001234"
        hint="Format: SBIN0001234"
        value={values?.ifsc}
        onChange={onChange ? (v) => onChange({ ifsc: v.toUpperCase() }) : undefined}
        error={ifscError}
      />
      {/* Plain language for the same promise. The old copy named the two techniques
          ("penny-drop / name-match check"), which is how WE verify, not anything the
          applicant can act on — same reason the method name stays outside the phone. */}
      <p className="mt-2 text-[14px] leading-tight text-[#666666]">
        Your details are checked directly with your bank. We do not store your personal information
        beyond what is required by law.
      </p>
    </>
  );
}
